import { useQueryClient } from "@tanstack/react-query";
import * as React from 'react'

import { useIdentity } from '@/lib/auth/hooks/identity'
import { usePrimaryServiceType } from '@/lib/auth/hooks/primary-service-type'
import { isOidcEnabled } from '@/lib/auth/oidc-bootstrap'
import { Permission, Role } from '@/lib/auth/roles'
import { getDecodedToken, getRefreshToken } from '@/lib/token/token'
import { useServiceType } from '@/stores/service-type'
import { useToken } from '@/stores/token'
import { Identity } from '@/types/identity'
import { QUERY_KEY_IDENTITY, QUERY_KEY_PRIMARY_SERVICE_TYPE } from "@/utils/keys";

export interface AuthContext {
  identity: Identity | null
  logout: (onLogout: () => void) => void
}

/** Upper bound on the best-effort logout revocation, so a slow or unreachable backend cannot hang logout. */
const LOGOUT_REVOKE_TIMEOUT_MS = 5000

const AuthContext = React.createContext<AuthContext | null>(null)

export function AuthenticationProvider({ children }: { children: React.ReactNode }) {
  const { data, dataUpdatedAt } = useIdentity()
  const { setToken, setPermissions } = useToken()
  const queryClient = useQueryClient()
  const { setServiceType } = useServiceType()

  usePrimaryServiceType(setServiceType)
  
  React.useEffect(() => {
    if (!localStorage.getItem('token')) {
      return
    }

    setToken(getDecodedToken())

    queryClient.invalidateQueries({queryKey: [QUERY_KEY_PRIMARY_SERVICE_TYPE]})
  }, [dataUpdatedAt, setToken, queryClient])

  React.useEffect(() => {
    setPermissions(data)
    queryClient.resetQueries({
      predicate: query => !query.queryKey.includes(QUERY_KEY_IDENTITY) && !query.queryKey.includes(QUERY_KEY_PRIMARY_SERVICE_TYPE),
    })
    queryClient.refetchQueries({
      predicate: query =>
        query.isActive() && !query.queryKey.includes(QUERY_KEY_IDENTITY) && !query.queryKey.includes(QUERY_KEY_PRIMARY_SERVICE_TYPE),
    })
  }, [data, setPermissions, queryClient])

  const logout = async (onLogout: () => void) => {
    const refreshToken = getRefreshToken()

    if (refreshToken !== null) {
      // Revoke with a direct call, deliberately bypassing the api-client's refresh-if-stale path:
      // logout must revoke the token we currently hold, not one a preflight refresh has just
      // rotated away. The endpoint authenticates on the refresh token alone (no vf-token needed).
      // Bounded with a timeout because revocation is best effort - a slow or unreachable backend
      // must never trap the user in a session they asked to leave. (The K-series SLO work adds a
      // Keycloak backchannel logout to this call.)
      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), LOGOUT_REVOKE_TIMEOUT_MS)

      try {
        await fetch(`${import.meta.env.VITE_USER_MANAGER_URL}/auth/logout`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
          signal: controller.signal,
        })
      } catch {
        // Network error, or aborted at the timeout - fall through and clear locally regardless.
      } finally {
        clearTimeout(timer)
      }
    }

    localStorage.clear()

    queryClient.removeQueries()

    if (isOidcEnabled()) {
      // RP-initiated logout: the Keycloak session must end too, or the /login shim would silently
      // sign the user straight back in. Skip onLogout - its client-side navigation must not race,
      // and override, this cross-origin redirect.
      window.location.href = `${import.meta.env.VITE_USER_MANAGER_URL}/auth/oidc/logout`

      return
    }

    onLogout()
  }

  return (
    <AuthContext.Provider value={{ identity: data, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuthentication() {
  const context = React.useContext(AuthContext)

  if (!context) {
    throw new Error('useAuthentication must be used within the AuthProvider')
  }
  
  return context
}

export function useAuthorisation() {
  const { token, permissions } = useToken()

  if (!token) {
    throw Error('Token not set')
  }

  type VerifyRoles = { allowedRoles: Role[] }
  type VerifyPermissions = { allowedPermissions: Permission[] }

  const verifyRoles = React.useCallback(({ allowedRoles }: VerifyRoles) => {
    if (allowedRoles && allowedRoles.length > 0) {
      return allowedRoles.some(role => token.roles.includes(role))
    }

    return true
  }, [token.roles])

  const verifyPermissions = React.useCallback(({ allowedPermissions }: VerifyPermissions) => {
    if (allowedPermissions && allowedPermissions.length > 0) {
      return allowedPermissions.every(permission => permissions.includes(permission))
    }

    return true
  }, [permissions])

  return { verifyRoles, roles: token.roles, verifyPermissions, permissions }
}

type AuthorisationProps = {
  children: React.ReactNode
  roles: Role[]
  permissions: Permission[]
  forbiddenFallback?: React.ReactNode
}

export const Authorisation = ({ children, roles, permissions, forbiddenFallback = null }: AuthorisationProps) => {
  const { verifyRoles, verifyPermissions } = useAuthorisation()

  const canAccess = verifyRoles({ allowedRoles: roles })
  const hasPermission = verifyPermissions({ allowedPermissions: permissions })

  if (!(canAccess && hasPermission)) {
    return forbiddenFallback
  }

  return children
}
